import styled from '@emotion/styled'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Empty, Spin, Typography, message } from 'antd'
import { FC, useState } from 'react'
import {
  useDeleteContactActivity,
  useLoadContactActivities,
} from 'hooks/useFactoryContactActivity'
import { FactoryContactActivityItem } from 'services/interface/factoryContactActivity.interface'
import ContactNoteModal from './ContactNoteModal'
import NoteCard from './NoteCard'

const REF_TYPE = 'agent'
const PROCESS_TYPE = 'customer'

interface ContactNoteProps {
  refId: string
  factoryId: string
}

const ContactNote: FC<ContactNoteProps> = ({ refId, factoryId }) => {
  // ─── State + data ──────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const { data, isLoading } = useLoadContactActivities({
    factory_id: factoryId || undefined,
    ref_id: refId,
    ref_type: REF_TYPE,
    process_type: PROCESS_TYPE,
    limit: 100,
  })
  const { mutate: deleteMutate, isPending: deleting } = useDeleteContactActivity()

  const notes = data?.data?.list ?? []

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null)
    setModalOpen(true)
  }

  const openEdit = (note: FactoryContactActivityItem) => {
    setEditingId(note.id)
    setModalOpen(true)
  }

  const handleDelete = (note: FactoryContactActivityItem) => {
    deleteMutate(note.id, {
      onSuccess: () => message.success('ลบบันทึกการติดต่อสำเร็จ'),
      onError: (error) =>
        message.error(error?.message || 'ลบบันทึกการติดต่อไม่สำเร็จ'),
    })
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingId(null)
  }

  return (
    <Wrapper>
      {/* ── หัวข้อ + ปุ่มเพิ่มบันทึก ── */}
      <HeaderRow>
        <Typography.Text strong>บันทึกการติดต่อ</Typography.Text>
        <Button
          type="primary"
          ghost
          size="small"
          htmlType="button"
          icon={<PlusOutlined />}
          onClick={openCreate}
        >
          เพิ่มบันทึก
        </Button>
      </HeaderRow>

      {/* ── รายการบันทึก ── */}
      {isLoading ? (
        <SpinWrapper>
          <Spin />
        </SpinWrapper>
      ) : notes.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="ยังไม่มีบันทึกการติดต่อ"
        />
      ) : (
        <NotesGrid>
          {notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              deleting={deleting}
              onEdit={openEdit}
              onDelete={handleDelete}
            />
          ))}
        </NotesGrid>
      )}

      {/* ── Modal เพิ่ม/แก้ไข ── */}
      <ContactNoteModal
        open={modalOpen}
        editingId={editingId}
        refId={refId}
        refType={REF_TYPE}
        processType={PROCESS_TYPE}
        factoryId={factoryId}
        onClose={closeModal}
      />
    </Wrapper>
  )
}

export default ContactNote

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  padding: 8px 16px 0 16px;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const SpinWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`

const NotesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 10px;
  align-items: start;

  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
`
